import * as React from 'react';
import { useState } from 'react';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';
import {
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material/';
import { AdditionalChannelType } from '@tff/types/tff/message-service';
import CustomDialog from './Dialogs';
import { IntlMessages } from '../util';
import { useMessages } from '../hooks/use-messages';
import { useBookingContext } from '../hooks/use-booking-context';

interface NoticeDialogProps {
  isNdcSystem: boolean;
  onClose: () => void;
}

const NoticeDialog: React.FC<NoticeDialogProps> = ({ isNdcSystem, onClose }) => {
  const intl = useIntl();
  const { recordLocator, bookingSource } = useBookingContext();
  const { messageServiceItems, sendMessage } = useMessages({ recordLocator, bookingSource });
  const [openAddNoticeDialog, setOpenAddNoticeDialog] = useState<boolean>(false);
  const [notice, setNotice] = useState<string>();

  const notices = messageServiceItems?.filter(m => m.type === 'NOTICE');

  const handleAddNotice = (additionalChannel?: AdditionalChannelType) => {
    setOpenAddNoticeDialog(false);
    if (notice) {
      sendMessage('NOTICE', 'ADD_NOTICE', 'Successful', undefined, notice, additionalChannel);
    }
    onClose();
  };

  const renderNoticeTable = () => (
    <TableContainer component={Paper}>
      <Table style={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell>
              <IntlMessages id="menu.content" />
            </TableCell>
            <TableCell>
              <IntlMessages id="menu.contentType" />
            </TableCell>
            <TableCell>
              <IntlMessages id="menu.createdAt" />
            </TableCell>
            <TableCell>
              <IntlMessages id="menu.createdBy" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {notices?.map((m) => (
            <TableRow key={m.itemKey ?? `${m.createdAt}-${m.content}`}>
              <TableCell>
                <Typography variant="body1" color="primary" align="left">
                  {m.content}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="primary" align="left">
                  {m.contentType}
                </Typography>
              </TableCell>
              <TableCell component="th" scope="row">
                <strong>{m.createdAt ? format(new Date(m.createdAt), 'dd.MM.yyyy HH:mm') : null}</strong>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="primary" align="left">
                  {m.createdBy}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <CustomDialog
        onCancel={onClose}
        onConfirm={() => {
          setOpenAddNoticeDialog(true);
        }}
        initialOpen
        width="lg"
        confirmButtonText={intl.formatMessage({ id: 'pages.flightCombination.addNote' })}
        cancelButtonText={intl.formatMessage({ id: 'pages.flightCombination.close' })}
        title={
          <Typography variant="h5" align="left">
            {intl.formatMessage({ id: 'pages.flightCombination.notesRecordlocator' })} <strong>{recordLocator}</strong>
          </Typography>
        }
      >
        {notices?.length === 0 ? (
          <Typography variant="h5" align="center">
            <IntlMessages id="pages.flightCombination.notesNotFound" />
          </Typography>
        ) : (
          renderNoticeTable()
        )}
      </CustomDialog>

      {openAddNoticeDialog && (
        <CustomDialog
          onCancel={() => {
            setOpenAddNoticeDialog(false);
            onClose();
          }}
          onConfirm={() => handleAddNotice()}
          onAmendFailure={() => handleAddNotice('AMEND_FAILURE')}
          onInvChanges={() => handleAddNotice('INVOLUNTARY_CHANGE')}
          additionalButtons={isNdcSystem}
          initialOpen
          width="lg"
          confirmButtonText={intl.formatMessage({ id: 'global.add' })}
          cancelButtonText={intl.formatMessage({ id: 'pages.flightCombination.close' })}
          title={
            <Typography variant="h5" align="left">
              {intl.formatMessage({ id: 'pages.flightCombination.addNote' })}
            </Typography>
          }
        >
          <TextField
            label={intl.formatMessage({ id: 'menu.notice.notice' })}
            placeholder={intl.formatMessage({ id: 'menu.notice.placeholder' })}
            onChange={event => setNotice(event.target.value)}
            fullWidth
          />
        </CustomDialog>
      )}
    </>
  );
};

export default NoticeDialog;
